/**
 * Hearing the chords, On the Go.
 *
 * Without an instrument the graph is a picture of music nobody has heard. This module is
 * the answer to that: tap a card, hear the chord. It is deliberately the *only* place in
 * the client that makes a sound.
 *
 * ## Samples, not a synthesiser
 *
 * A detuned-oscillator "electric piano" is a few lines and sounds like a few lines. The
 * app sits beside a real piano, so the sound it makes when there is no piano should be a
 * piano: Salamander Grand, self-hosted under `public/audio/salamander/`, one sample per
 * octave (A1–A6, 412 KB the whole set) with Tone.js's `Sampler` pitch-shifting between
 * them. That is the same arrangement the owner's other harmony app uses.
 *
 * ## Why everything is behind a dynamic import
 *
 * Tone.js is not small, and the majority of sessions are at the piano where this module
 * is never used at all. `import('tone')` therefore happens on the first play — the same
 * trick `commandIntentParse.ts` uses for Zod and the registry uses for Essentia — so none
 * of it lands in the main chunk and none of it is downloaded by a player who never taps a
 * card.
 *
 * ## The context, and rule 10
 *
 * Rule 10 says On the Go constructs no `AudioContext`. That rule is about the *capture*
 * side: a context created outside a gesture is permanently suspended on iOS, and asking a
 * person on a train for their microphone is the failure it exists to prevent. Playback is
 * the opposite case — it exists only because someone tapped — so the context is created
 * inside `Tone.start()` on the first play, which is always on a tap's own stack, and never
 * before. Importing `tone` constructs no context by itself — checked against tone@15, and
 * the reason `warmUpChordPlayback` is safe to call before anyone has played anything.
 *
 * ## What is testable
 *
 * Everything above the sampler: which notes a chord becomes, in which octaves, spelled the
 * way a sampler wants them. Those are pure functions with their own tests. The sampler
 * itself is behind `ChordPlayerFactory`, which a test replaces — Tone cannot run under
 * jsdom and pretending otherwise would test the fake.
 */

import type { CanonicalChord, PitchClass } from '@hc/shared';

// ---------------------------------------------------------------------------
// Voicing: pitch classes to actual notes
// ---------------------------------------------------------------------------

/** Middle C. The chord is voiced around it, which is where a piano sounds most like one. */
export const MIDDLE_C_MIDI = 60;

/**
 * How far above middle C a root is allowed to sit before the whole voicing drops an
 * octave. Roots from C to E stay at or above middle C; F upwards fall below it, so the
 * close voicing above them still lands in the octave people think of as the middle of the
 * keyboard rather than climbing into the treble.
 */
export const ROOT_CEILING_PC = 4;

/** Sharps only. The sampler does not care about spelling, and every pitch class has one. */
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/** A MIDI note number as a Tone.js note name: 60 → "C4", 61 → "C#4". */
export function midiToNoteName(midi: number): string {
  const rounded = Math.round(midi);
  const pc = ((rounded % 12) + 12) % 12;
  // MIDI 60 is C4 in scientific pitch notation, which is what Tone.js parses.
  const octave = Math.floor(rounded / 12) - 1;
  return `${SHARP_NAMES[pc] as string}${octave}`;
}

/** Where the root of this chord sits: middle C's octave, or the one below it. */
export function rootMidi(rootPc: PitchClass): number {
  const above = MIDDLE_C_MIDI + rootPc;
  return rootPc > ROOT_CEILING_PC ? above - 12 : above;
}

/**
 * The chord as MIDI notes: a close voicing stacked upward from the root, with the sounding
 * bass an octave below it.
 *
 * Close rather than spread because every note comes from the same sampled instrument and a
 * wide voicing of pitch-shifted samples reads as an effect rather than as a piano. The
 * bass is placed from `bassPc` rather than the root, so a first-inversion chord sounds like
 * one — which matters here, because `CanonicalChord.bassPc` is the only part of an
 * inversion the graph ever shows.
 *
 * Ascending, which is also the order the roll strikes them in.
 */
export function voiceChord(chord: CanonicalChord): number[] {
  const root = rootMidi(chord.rootPc);
  const upper = chord.pitchClasses.map((pc) => root + mod12(pc - chord.rootPc));
  // Below the cluster by construction: the bass pitch class placed in the octave under the
  // root, so it can never collide with or rise above the voicing sitting on top of it.
  const bass = root - 12 + mod12(chord.bassPc - chord.rootPc);
  return [...new Set([bass, ...upper])].sort((a, b) => a - b);
}

/** The chord as note names, ready for `triggerAttackRelease`. */
export function chordNoteNames(chord: CanonicalChord): string[] {
  return voiceChord(chord).map(midiToNoteName);
}

function mod12(value: number): number {
  return ((value % 12) + 12) % 12;
}

// ---------------------------------------------------------------------------
// The player seam
// ---------------------------------------------------------------------------

/** How long the chord rings. Long enough to hear it settle, short enough to tap again. */
export const CHORD_DURATION_S = 1.4;

/**
 * The gap between notes of the roll.
 *
 * A chord struck perfectly simultaneously sounds synthetic; twenty milliseconds of spread,
 * low to high, is roughly what two hands do and is the difference between "a sample
 * played" and "a piano". Small enough that nobody would call it an arpeggio.
 */
export const ROLL_STAGGER_S = 0.02;

/** Salamander Grand, self-hosted. One sample per octave; Tone pitch-shifts between them. */
export const SAMPLE_BASE_URL = '/audio/salamander/';
export const SAMPLE_URLS: Readonly<Record<string, string>> = Object.freeze({
  A1: 'A1.mp3',
  A2: 'A2.mp3',
  A3: 'A3.mp3',
  A4: 'A4.mp3',
  A5: 'A5.mp3',
  A6: 'A6.mp3',
});

/** Output level. The samples are hot, and this sits under a conversation. */
export const OUTPUT_VOLUME_DB = -6;

/**
 * The one thing this module needs from Tone.js.
 *
 * Structural, so a test supplies four lines instead of a browser — and so the whole of
 * `tone` stays behind one `import()` that a test never triggers.
 */
export interface ChordPlayer {
  /** Strike these notes, ascending, `stagger` seconds apart, ringing for `duration`. */
  strike(notes: readonly string[], options: { duration: number; stagger: number }): void;
  dispose(): void;
}

export type ChordPlayerFactory = () => Promise<ChordPlayer>;

/**
 * Build the real player: load Tone, unlock the context, fetch the samples.
 *
 * `Tone.start()` must run on the stack of the tap that caused the first play, which is why
 * `playChord` is called synchronously from the store subscription rather than after an
 * await. Everything after it is ordinary loading.
 */
const createTonePlayer: ChordPlayerFactory = async () => {
  const Tone = await import('tone');
  await Tone.start();

  const sampler = new Tone.Sampler({
    urls: { ...SAMPLE_URLS },
    baseUrl: SAMPLE_BASE_URL,
    release: 1.2,
  }).toDestination();
  sampler.volume.value = OUTPUT_VOLUME_DB;
  // Resolves when every registered buffer has downloaded — 412 KB, once per session.
  await Tone.loaded();

  return {
    strike(notes, { duration, stagger }) {
      // A hair in the future: scheduling at exactly `now` is the one way to get a click.
      const at = Tone.now() + 0.05;
      notes.forEach((name, index) => {
        // Each note rings until the end of the chord rather than for a fixed length, so
        // the roll spreads the attacks without ragging the release.
        const hold = Math.max(0.2, duration - index * stagger);
        sampler.triggerAttackRelease(name, hold, at + index * stagger);
      });
    },
    dispose() {
      sampler.dispose();
    },
  };
};

let factory: ChordPlayerFactory = createTonePlayer;
let pending: Promise<ChordPlayer | null> | null = null;
let player: ChordPlayer | null = null;

/**
 * Whether this environment can make a sound at all.
 *
 * Checked before the dynamic import rather than after, so a browser with no Web Audio —
 * and a jsdom test, which is the same thing — never downloads Tone.js to find out. The
 * probe matches `capabilities.ts`, including the `webkit` prefix that iOS still needs.
 */
function canPlay(): boolean {
  if (factory !== createTonePlayer) return true;
  const env = globalThis as { AudioContext?: unknown; webkitAudioContext?: unknown };
  return typeof env.AudioContext === 'function' || typeof env.webkitAudioContext === 'function';
}

/** Swap the player. Tests only; `null` restores the real one. */
export function setChordPlayerFactory(next: ChordPlayerFactory | null): void {
  dispose();
  factory = next ?? createTonePlayer;
}

/**
 * Load Tone and the samples without making a sound.
 *
 * Called when the player enters On the Go with sound on — itself a tap — so that the
 * first chord they choose plays immediately instead of after a chunk download. A failure
 * here is not reported: the first `playChord` will try again and is equally silent about
 * it.
 */
export function warmUpChordPlayback(): void {
  void ensurePlayer();
}

function ensurePlayer(): Promise<ChordPlayer | null> {
  if (player) return Promise.resolve(player);
  if (!canPlay()) return Promise.resolve(null);
  pending ??= factory()
    .then((built) => {
      player = built;
      return built;
    })
    .catch(() => {
      // Offline, samples missing, audio refused — all the same answer. Silence is an
      // acceptable degradation for a feature that exists to be pleasant, and clearing
      // `pending` means the next tap retries rather than being silent forever.
      pending = null;
      return null;
    });
  return pending;
}

/**
 * Play a chord.
 *
 * Fire-and-forget by design: nothing in the session may wait on a sound, and a chord that
 * fails to play must not surface anywhere. The returned promise is for tests.
 */
export async function playChord(chord: CanonicalChord): Promise<void> {
  const notes = chordNoteNames(chord);
  if (notes.length === 0) return;
  const ready = await ensurePlayer();
  ready?.strike(notes, { duration: CHORD_DURATION_S, stagger: ROLL_STAGGER_S });
}

/** Hand back the sampler and its buffers. */
export function dispose(): void {
  player?.dispose();
  player = null;
  pending = null;
}
